/**
 * Block registry.
 *
 * Each entry describes everything the editor needs to know about a block type:
 *   schema       – defaults() + optional validate()
 *   capabilities – isContainer, allowedChildren, canDropInto
 *   ui           – Canvas, Inspector, icon, label, keywords
 */

const _registry = {};

/**
 * Register a block type definition.
 * @param {object} definition
 */
export function registerBlock(definition) {
  if (!definition || !definition.type) {
    throw new Error('Block definition must have a `type` field.');
  }
  _registry[definition.type] = definition;
}

/**
 * Retrieve a block type definition.
 * @param {string} type
 * @returns {object|undefined}
 */
export function getBlockDefinition(type) {
  return _registry[type];
}

/**
 * Return all registered block definitions as an array.
 * @returns {object[]}
 */
export function getAllBlockDefinitions() {
  return Object.values(_registry);
}

/**
 * Return true if `type` is registered as a container.
 */
export function isContainerType(type) {
  const def = _registry[type];
  return !!(def && def.capabilities && def.capabilities.isContainer);
}

/**
 * Return true if `blockType` is allowed inside `parentType`.
 */
export function canDropInto(blockType, parentType) {
  const parentDef = _registry[parentType];
  if (!parentDef || !parentDef.capabilities || !parentDef.capabilities.isContainer) {
    return false;
  }
  const { allowedChildren } = parentDef.capabilities;
  if (!allowedChildren) return true; // unrestricted
  if (typeof allowedChildren === 'function') return allowedChildren(blockType);
  if (Array.isArray(allowedChildren)) return allowedChildren.includes(blockType);
  return true;
}
